import React from "react";
import { isCarrierAccessEnabled } from "@shared/config/featureFlags";
import {
  accessTypeLabel,
  carrierAccessGroups,
  type CarrierAccess,
  type CarrierAccessGroup,
} from "kontrolEdge/helpers/carrierAccess";
import { DetailLink } from "../Shared/DetailLink";
import { carrierAccessDetailPath } from "./extenderPopCarrierAccessData";

/** The id of the access, linked to its detail while Carrier Access is on. */
const AccessId: React.FC<{ group: CarrierAccessGroup }> = ({ group }) => {
  if (!group.access) {
    return <span className="text-semantic-text-secondary">Not declared</span>;
  }
  return isCarrierAccessEnabled() ? (
    <DetailLink
      to={carrierAccessDetailPath(group.access.id)}
      label={group.access.serviceId}
    />
  ) : (
    <span>{group.access.serviceId}</span>
  );
};

/**
 * The Carrier Access of a site, one entry per sold access: "84-7C1E20 /
 * Type: Dual", and under it its carriers with their medium (CTX-800). The
 * carriers no access claimed yet close the list as "Not declared". Shared by
 * the Extended PoP list and detail so both read the same.
 */
const CarrierAccessSummary: React.FC<{
  carriers: CarrierAccess[];
  align?: "start" | "end";
}> = ({ carriers, align = "start" }) => {
  const groups = carrierAccessGroups(carriers);
  if (groups.length === 0) return <div>-</div>;

  return (
    <div
      className={`flex flex-col gap-1.5 leading-tight ${
        align === "end" ? "items-end text-right" : ""
      }`}
    >
      {groups.map((group) => (
        <div key={group.access?.id ?? "undeclared"} className="flex flex-col">
          <span className="whitespace-nowrap text-sm">
            <AccessId group={group} />
            {group.access && (
              <span className="text-xs text-semantic-text-secondary">
                {" "}
                / Type:{" "}
                {accessTypeLabel(group.access.accessType)}
              </span>
            )}
          </span>
          <span className="text-xs text-semantic-text-secondary">
            {group.carriers
              .map((carrier) => `${carrier.name} · ${carrier.medium}`)
              .join(", ")}
          </span>
        </div>
      ))}
    </div>
  );
};

export default CarrierAccessSummary;
